import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store/rootReducer";

export type ModalStatus = "modal" | "form" | "hide" | "edit" | "add";

interface FormConfig {
  onSubmit?: (data: any) => void;
  defaultValues?: any;
  submitText?: string;
}
interface ModalProps {
  name?: string;
  status?: any;
  key?: any;
  onClose?: (name: string) => void;
  title?: string;
  children?: React.ReactNode;
  className?: string;
  header?: React.ReactNode;
  outerClassName?: string;
  form?: FormConfig;
  formLoading?: boolean;
  data?: any;
  url?: string | null;
}

export interface RightPanelState {
  name: string;
  status: "show" | "hide";
  data: unknown;
  title: string;
}

export interface RightPanelPayload {
  name: string;
  data?: unknown;
  title?: string;
}

interface UiState extends ModalProps {
  quoteKey: number;
  stepIndex: number;
  rightPanel: RightPanelState;
}

const initialState: UiState = {
  name: "",
  status: "hide",
  onClose: () => {},
  title: "",
  children: null,
  className: "",
  header: undefined,
  outerClassName: "",
  quoteKey: 0,
  stepIndex: 1,
  form: {
    onSubmit: null,
    defaultValues: null,
    submitText: "",
  },
  formLoading: false,
  data: null,
  url: null,
  rightPanel: {
    name: "",
    status: "hide",
    data: null,
    title: "",
  },
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    openModal(state, action: { payload: ModalProps }) {
      state.name = action.payload.name;
      state.status = action.payload.status || "modal";
      state.onClose = action.payload.onClose || (() => {});
      state.title = action.payload.title || "";
      state.children = action.payload.children || null;
      state.data = action.payload.data || null;
      state.className = action.payload.className || "";
      state.header = action.payload.header || undefined;
      state.outerClassName = action.payload.outerClassName || "";
      state.form = action.payload.form ?? initialState.form;
      state.url = action.payload.url || null;
    },
    closeModal(state) {
      state.status = "hide";
    },
    setStepIndex(state, action) {
      state.stepIndex = action.payload;
    },
    setFormLoading(state, action) {
      state.formLoading = action.payload;
    },
    openRightPanel(state, action: PayloadAction<RightPanelPayload>) {
      state.rightPanel.status = "show";
      state.rightPanel.name = action.payload.name;
      state.rightPanel.data = action.payload.data ?? null;
      state.rightPanel.title = action.payload.title ?? "";
    },
    closeRightPanel(state) {
      state.rightPanel.status = "hide";
    },
    toggleRightPanel(state, action: PayloadAction<RightPanelPayload>) {
      const isSamePanel = state.rightPanel.name === action.payload.name;

      if (state.rightPanel.status === "show" && isSamePanel) {
        state.rightPanel.status = "hide";
        return;
      }

      state.rightPanel.status = "show";
      state.rightPanel.name = action.payload.name;
      state.rightPanel.data = action.payload.data ?? null;
      state.rightPanel.title = action.payload.title ?? "";
    },
    setRightPanelData(state, action: PayloadAction<unknown>) {
      state.rightPanel.data = action.payload;
    },
  },
});

export const {
  openModal,
  closeModal,
  setStepIndex,
  setFormLoading,
  openRightPanel,
  closeRightPanel,
  toggleRightPanel,
  setRightPanelData,
} = uiSlice.actions;

export const selectRightPanel = (state: RootState) => state.ui.rightPanel;
export const selectIsRightPanelOpen = (panelId: string) => (state: RootState) =>
  state.ui.rightPanel.status === "show" && state.ui.rightPanel.name === panelId;

export const { reducer: uiReducer } = uiSlice;
